import { useState } from "react";
import {
  MessageCircle,
  Phone,
  Facebook,
  Mail,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BUSINESS, SERVICES } from "@/data/cybernet";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Completa nombre, teléfono y mensaje.");
      return;
    }
    const service =
      SERVICES.find((s) => s.id === form.service)?.title || "General";
    const text = `Hola CYBERNET, mi nombre es ${form.name}.
Teléfono: ${form.phone}${form.email ? "\nCorreo: " + form.email : ""}
Servicio de interés: ${service}

Mensaje:
${form.message}`;
    const url = `https://wa.me/${BUSINESS.primaryWhatsapp}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    toast.success("Abriendo WhatsApp con tu mensaje...");
  };

  const socials = [
    {
      id: "wa",
      icon: MessageCircle,
      label: "WhatsApp",
      href: `https://wa.me/${BUSINESS.primaryWhatsapp}`,
      color: "bg-[#E10600] text-white",
    },
    {
      id: "fb",
      icon: Facebook,
      label: "Facebook",
      href: BUSINESS.facebook,
      color: "bg-[#0A0A0A] text-white",
    },
    {
      id: "email",
      icon: Mail,
      label: "Correo",
      href: `mailto:${BUSINESS.email}`,
      color: "border-2 border-[#0A0A0A] text-[#0A0A0A]",
    },
    {
      id: "call",
      icon: Phone,
      label: "Llamar",
      href: `tel:+${BUSINESS.primaryWhatsapp}`,
      color: "border-2 border-[#0A0A0A] text-[#0A0A0A]",
    },
  ];

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="bg-[#FAFAFA] py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="uppercase text-xs font-bold tracking-[0.25em] text-[#E10600] mb-4">
              — Contacto
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#0A0A0A] mb-6">
              Cuéntanos qué
              <br />
              <span className="text-[#E10600]">necesitas.</span>
            </h2>
            <p className="text-[#52525B] leading-relaxed mb-10 max-w-md">
              Llena el formulario y te contactamos por WhatsApp con tu mensaje
              listo. También puedes escribirnos directo en nuestras redes.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {socials.map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${s.id}`}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-sm transition-transform hover:-translate-y-0.5 ${s.color}`}
                >
                  <s.icon className="h-4 w-4" />
                  {s.label}
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white border border-[#E4E4E7] p-8 md:p-10 rounded-sm space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-widest text-[#0A0A0A]">
                    Nombre *
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={(e) => onChange("name", e.target.value)}
                    className="mt-2 rounded-sm border-[#E4E4E7] focus-visible:ring-[#E10600]"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-widest text-[#0A0A0A]">
                    Teléfono *
                  </Label>
                  <Input
                    id="phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => onChange("phone", e.target.value)}
                    className="mt-2 rounded-sm border-[#E4E4E7] focus-visible:ring-[#E10600]"
                    placeholder="411-000-0000"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-[#0A0A0A]">
                  Correo electrónico
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  className="mt-2 rounded-sm border-[#E4E4E7] focus-visible:ring-[#E10600]"
                  placeholder="tucorreo@ejemplo.com"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#0A0A0A]">
                  Servicio de interés
                </Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => onChange("service", v)}
                >
                  <SelectTrigger
                    data-testid="contact-select-service"
                    className="mt-2 rounded-sm border-[#E4E4E7] focus:ring-[#E10600]"
                  >
                    <SelectValue placeholder="Selecciona un área" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`contact-service-${s.id}`}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-xs font-semibold uppercase tracking-widest text-[#0A0A0A]">
                  Mensaje *
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={(e) => onChange("message", e.target.value)}
                  className="mt-2 rounded-sm border-[#E4E4E7] focus-visible:ring-[#E10600] min-h-[120px]"
                  placeholder="¿En qué te podemos ayudar?"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#E10600] text-white text-sm font-semibold px-6 py-4 rounded-sm hover:bg-[#B30500] transition-colors"
              >
                <Send className="h-4 w-4" />
                Enviar por WhatsApp
              </button>
              <p className="text-xs text-[#52525B] text-center">
                Al enviar, se abrirá WhatsApp con tu mensaje listo para
                confirmar.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
